import { PullResult, PushResult } from '@shared/providers/provider-transport';
import Highland from 'highland';
import { DataField } from '@shared/providers/types';

void Highland;

export type WorkbookApiFactory = (context: any) => WorkbookApi<any>;

export type IndexedRow = [number, ...any[]];

export interface WorkbookApi<R> {
  context: any;
  getColumnHeaders(range: R): Promise<string[]>;
  readRangeData(range: R, options?: RangeReadingOptions): Promise<Highland.Stream<IndexedRow>>;
  writeRangeResult(
    range: R,
    resultStream: Highland.Stream<PushResult>,
    options?: ResultWritingOptions,
  ): Promise<R | null>;
  detectRangeByCell(worksheetId: string, topLeftCellAddress: string, options?: { trimToCell?: boolean }): Promise<R | null>;
  getRangeBySize(worksheetId: string, topLeftCellAddress: string, rowCount: number, columnCount: number): Promise<R>;
  writeRangeData(range: R, dataStream: Highland.Stream<PullResult>, options?: RangeWritingOptions): Promise<number>;
  createNewWorksheet(worksheetName: string, options?: CreateWorksheetOptions): Promise<string>;
  appendRangeData(range: R, dataStream: Highland.Stream<any[]>, options?: RangeWritingOptions): Promise<number>;
  createTable(range: R, options?: CreateTableOptions): Promise<void>;
  autofitColumns(range: R): void;
  clearRangeData(range: R): Promise<void>;
  insertColumnsAfter(range: R, columnCount: number): Promise<R>;
  insertColumnsAt(range: R): Promise<R>;
  addRows(range: R, rowCount: number): R;
  getRangeByAddress(addressStr: any): Promise<R | null>;
  getRangeAddressInfo(range: R): Promise<{ worksheetId: string; address: string }>;
  getRangeValues(range: R): Promise<any[][]>;
  getRangeColumn(range: R, columnIndex: number): R;
  getRangeCellValue(range: R, rowIndex: number, colIndex: number): Promise<any>;
  getWorksheet?(worksheetId: string): Promise<any>;
  writeRangeValue(range: R, values: any[][]): Promise<void>;
  calcRowCountToRead(range: R, options?: RangeReadingOptions): Promise<number>;
  truncateRangeRows(range: R, rowCount: number): Promise<void>;
  applyPickLists(tableRange: R, flowstepId: string, fields: DataField[]): Promise<void>;
  clearFilters(worksheetId: string): Promise<any[]>;
  applyFilters(worksheetId: string, sources: any[]): Promise<void>;
  /* Methods below are optional for implementation */
  disableProtection?(worksheetId: string, password?: string): Promise<any>;
  enableProtection?(worksheetId: string, protectionOptions: any, password?: string): Promise<void>;
}

export type CreateWorksheetOptions = {
  activate: boolean;
};

export type RangeReadingOptions = {
  visibleOnly?: boolean;
  selectedOnly?: boolean;
  columnsToRead?: number[];
  rowSize?: number;
  specifiedRowNumbers?: [number, number][];
};

export enum InsertBehaviour {
  never,
  always,
  afterEnd,
}

export type RangeWritingOptions = {
  insertRowsMode?: InsertBehaviour;
  insertColumnCount?: number;
  rowNumberFormats?: (string | null)[];
};

export type ResultWritingOptions = {
  idColumnIndex?: number;
  idFormat?: string;
  writeRowStates?: boolean;
};

export type CreateTableOptions = {
  resizeExistent?: boolean;
};

export const ResultColumnIndices = {
  OperationResult: -1,
  DateTime: -2,
};
